import React, { useMemo, useState } from 'react';
import { getLocale } from 'umi';
import { Segment, Tabs } from 'tea-component';
import CardCommon from '@op/components/CardCommon';
import useLanguage from '@op/hooks/useLanguage';
import SortBySelect from '@op/components/FilterBoxCommon/filters/single-select-factory';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import ChannelLabel from '@op/components/ChannelLabel';
import FigureCard from '@op/pages/Overview/TrendingPublications/components/FigureCard';
import { useStudioTrendingRequest } from './useStudioTrendingRequest';
import { LANGUAGE_EN } from '@home/_config/config';
import { getLanguage } from '@home/utils/common';
import SvgIcon from '@home/components/SvgIcon';
import { useToOpinionPage } from '../utils';
import { distinguishOfficialAccounts } from '@op/components/FilterCommon/utils/monitorUtil';
import { addStudioChannel } from './utils';
import './index.scss';
import { LanguageType } from '@op/types/common';
import TranslateSelect from '@op/components/SelectComponents/TranslateSelect';
import { languageSelectOptions } from '@op/components/FilterBoxCommon/constant';

const sortByOptions = [
  { value: 'view', text: 'View' },
  { value: 'engagement', text: 'Engagement' },
  { value: 'follower_number', text: 'Followers' },
  { value: 'comment_time', text: 'Date' },
];

const tabItemRender = (isEn: boolean, nameEn: any, nameCn: any, icon?: string) => <div className='dbh-trend-tab'>
  {icon && <SvgIcon className='dbh-trend-tab-icon' type={icon} />}
  <span className='dbh-trend-tab-text'>{getLanguage(isEn, nameEn, nameCn)}</span>
</div>;

const buttonItemRender = (item: any) => {
  const { value, text } = item;
  return {
    value,
    text: value === 'all' ? text :
      <div className='dbh-select-channel'>
        <ChannelLabel
          className="dbh-select-channel-icon"
          channel_name={value}
          showText={true}
        ></ChannelLabel>
      </div>
  };
};

export default function StudioTrendingVideo(props: any) {
  const {
    filter: propsFilter,
    filterCond,
    language: propsLanguage,
    wholeGameInfo,
    tabs: propsTabs,
    config
  } = props || {};

  const defaultLanguage = propsLanguage ? propsLanguage : config?.language;
  const [language, setLanguage] = useState<LanguageType>(defaultLanguage || 'origin');
  const { className = 'dbh-studio-trend-video', style } = props || {};
  const locale = getLocale();
  const isEn = locale === LANGUAGE_EN;
  const { formatMessage, formatOptionsMessage } = useLanguage();
  const tabs = propsTabs || [
    {
      id: "video", label: tabItemRender(isEn, "Video Platforms", "视频平台", "studio-video")
    },
    {
      id: "social", label: tabItemRender(isEn, "Social Media", "社交媒体", "social-media")
    },
    {
      id: "official", label: tabItemRender(isEn, "Official Account", "官方账号", "offical-accounts")
    },
  ];

  const [activeId, setActiveId] = useState(tabs?.[0]?.id || 'video');
  const [segmentValue, setSegmentValue] = useState('all');
  const [isMore, setIsMore] = useState(true);
  const [sortBy, setSortBy] = useState({
    by: 'view',
    order: 'desc',
  });

  const sortByChannel = (list: any[], sortList: string | any[]) => {
    const newList = list?.slice()?.sort((a, b) => {
      const indexA = sortList?.indexOf(a);
      const indexB = sortList?.indexOf(b);
      if (indexA === -1 && indexB === -1) return 0; // 如果两个元素都不在sortList中，保持原顺序
      if (indexA === -1) return 1; // 如果a不在sortList中，排在后面
      if (indexB === -1) return -1; // 如果b不在sortList中，排在前面
      return indexA - indexB; // 按照sortList中的顺序排序
    });
    return newList;
  };

  const { options, videoList, socialList } = useMemo(() => {
    // 筛选器渠道数据
    const channelList = filterCond?.channel?.social || [];
    // 视频渠道
    const videoChannel = ['youtube', 'twitch', 'tiktok', 'bilibili', 'douyin', 'huya'];
    const socialChannel = ["twitter", "reddit", "pathofexile", "discord", "steam_community", "instagram", "facebook", "forumgamer", "heybox", "tieba", "3dm", "gamersky"];
    // Video Platforms
    const video: any = [];
    // Social Media
    const social: any = [];
    channelList?.forEach((v: string) => {
      if (videoChannel?.includes(v)) {
        video?.push(v);
      } else {
        social?.push(v);
      }
    });
    const videoList = sortByChannel(video, videoChannel);
    const socialList = sortByChannel(social, socialChannel);
    const segmentMap: any = {
      video: videoList?.length ? [
        { value: 'all', text: 'All' },
        ...(videoList?.map((item: any) => buttonItemRender({ value: item })) || []),
      ] : [],
      social: socialList?.length ? [
        { value: 'all', text: 'All' },
        ...(socialList?.map((item: any) => buttonItemRender({ value: item })) || []),
        ...(socialList?.length > 3 ?
          [{
            value: 'more', text: isMore ?
              <div className='dbh-trend-segment-more'>{isEn ? "More" : "展开"}<SvgIcon type={"fill-icon-arrow"} /></div> :
              <div className='dbh-trend-segment-less'><SvgIcon type={"fill-icon-arrow"} />{isEn ? "Less" : "收起"}</div>
          }] : [])
      ]?.filter((v, index) => isMore ? (index <= 3 || v.value === 'more') : true) : [],
      official: [],
    };
    const options = segmentMap?.[activeId] || [];
    return {
      options,
      videoList,
      socialList,
    };
  }, [activeId, filterCond, isMore, isEn]);

  const getExtraFilter = (activeId: string, segmentValue: string, sortBy: { by: string; order: string; }) => {
    const extraFilter: any = {};
    // 舆情官号处理逻辑
    const account = distinguishOfficialAccounts(
      filterCond?.monitor_by_account_category?.monitory || {},
    );
    if (activeId === 'official') {
      extraFilter.except_social_media_by_account = undefined;
      extraFilter.social_media_by_account = account?.['official-accounts'] || [];
      extraFilter.social_media_by_keyword = ["-99999"];
      extraFilter.channel = [];
      // 官方账号
      extraFilter.organization = "official";
    } else {
      extraFilter.except_social_media_by_account = account?.['official-accounts'] || [];
      extraFilter.social_media_by_account = account?.others || [];
      extraFilter.social_media_by_keyword = undefined;
      // 非官方账号
      extraFilter.organization = "player";
      // 渠道处理逻辑
      if (activeId === 'video') {
        extraFilter.channel = addStudioChannel(segmentValue === 'all' ? videoList : [segmentValue]);
      }
      if (activeId === 'social') {
        extraFilter.channel = addStudioChannel(segmentValue === 'all' ? socialList : [segmentValue]);
      }
    }
    // Social Media和Official Account需要请求视频和贴子聚合数据top5(不传media_types和review_types)
    if (activeId === 'video') {
      extraFilter.media_types = ['video', 'live'];
      extraFilter.review_types = ['posts_video', 'stream_titles'];
    } else {
      extraFilter.media_types = [];
      extraFilter.review_types = [];
    }
    // 排序处理
    const { by = 'comment_time', order = 'desc' } = sortBy || {};
    extraFilter.sort_item = by;
    extraFilter.sort_model = order;
    return extraFilter;
  };

  const filter = useMemo(() => {
    if (!filterCond) return {};
    const { entity_image, isConfirm, studioid, ...restFilter } = propsFilter || {};
    const newFilter = {
      ...restFilter,
      region: [],
      region_type: "region",
      logic: "and",
      language: [],
      channel: [],
      channel_type: "social",
      social_media_by_account: undefined,
      social_media_by_keyword: undefined,
      keyword: [],
      keyword_filter_types: [],
      ...(getExtraFilter(activeId, segmentValue, sortBy)),
    };
    return newFilter;
  }, [filterCond, propsFilter]);

  const { data, loading, refetch } = useStudioTrendingRequest({ filter });

  const { goToOtherPage } = useToOpinionPage({
    filter: { ...filter, ...(getExtraFilter(activeId, segmentValue, sortBy)) },
    key: 'Trending Publications',
  });

  const empty = !data?.list || data?.list?.length === 0;
  const canJump = !loading;
  function goOtherPage() {
    canJump && goToOtherPage({});
  }

  return (
    <CardCommon
      className={className}
      bodyStyle={style}
      title={<>
        <div className='dbh-trend-card-flex' onClick={goOtherPage}>
          <span className="dbh-trend-card-title">{formatMessage({ id: 'Trending Publications' })}</span>
          {canJump && <SvgIcon type="studio-arrow" className="dbh-trend-card-title-icon" />}
        </div>
        <Tabs
          tabs={tabs}
          activeId={activeId}
          onActive={(tabs: any) => {
            if (activeId === tabs?.id) return;
            setActiveId(tabs?.id);
            setSegmentValue('all');
            // 请求接口
            const extraFilter = getExtraFilter(tabs?.id, "all", sortBy);
            refetch(extraFilter);
          }}
        />
        <div className='dbh-trend-video-list-header'>
          <SortBySelect
            defaultParams={{
              options: sortByOptions,
              btnTextPrefix: formatMessage({ id: 'Sort By' }),
            }}
            defaultValue={sortBy.by}
            onChange={(_key: any, val: string) => {
              if (sortBy.by === val) return;
              setSortBy({ by: val, order: 'desc' });
              buriedBtnPoint('TPTV_Sort', { by: val, order: 'desc' });
              // 请求接口
              const extraFilter = getExtraFilter(activeId, segmentValue, { by: val, order: 'desc' });
              refetch(extraFilter);
            }}
          />
          {
            options?.length > 0 ? <Segment
              value={segmentValue}
              onChange={(value: string) => {
                if (segmentValue === value) return;
                if (value === 'more') {
                  return setIsMore(!isMore);
                }
                setSegmentValue(value);
                // 请求接口
                const extraFilter = getExtraFilter(activeId, value, sortBy);
                refetch(extraFilter);
              }}
              options={options}
            /> : <div style={{ height: '44px' }}></div>
          }
        </div>
      </>}
      loading={loading || !filterCond}
      loadingHeight={361}
      emptyHeight={361}
      // 无数据或者Video Platforms下没有默认视频渠道
      empty={activeId === 'video' ? empty || !videoList?.length : empty}
      alwaysShowOperations
      dropdownsFilterBeforeElement={
        <div className='dbh-trend-card-translate'>
          <TranslateSelect
            langOptions={formatOptionsMessage(languageSelectOptions)}
            lang={language}
            isSingle={true}
            getChange={(e: any) => {
              setLanguage(e);
            }}
          />
        </div>
      }
    >
      <div key={activeId} className={`dbh-trend-video-list-wrapper`}>
        <div className="dbh-trend-video-list">
          {data?.list?.length > 0 &&
            data?.list?.map((item: any, index: number) => (
              <div className={`dbh-trend-video-item ${["video", "live"]?.includes(item?.media_type) ? "dbh-trend-video-item-video" : "dbh-trend-video-item-posts"}`} key={index}>
                <FigureCard
                  size="small"
                  item={item}
                  wholeGameInfo={wholeGameInfo}
                  language={language}
                ></FigureCard>
              </div>
            ))}
        </div>
      </div>
    </CardCommon>
  );
}
